import Heading from "@tiptap/extension-heading";
import { Plugin, PluginKey} from 'prosemirror-state';
import { Node, Slice, Fragment, DOMSerializer, DOMParser } from "prosemirror-model";
import {generateUUID2, getImagePathSvg, getImagePathWithThemeSvg, imgPathPerfix} from "../../../utils";
import { Extension, markPasteRule, mergeAttributes } from '@tiptap/core'
import { CellSelection } from "prosemirror-tables";

export const getHeadingExtend = (from:any = '')=>{

    function extractPlainTextWithNewlines(el: HTMLElement): string {
        const blockTags = new Set(['P', 'DIV', 'LI', 'BR', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4'])

        let text = ''
        function traverse(node: Node) {
            if (node.nodeType === 3) {
                text += node.textContent
            } else if (node.nodeType === 1) {
                const element = node as HTMLElement
                const tag = element.tagName

                if (blockTags.has(tag) && text && !text.endsWith('\n')) {
                    text += '\n'
                }

                Array.from(element.childNodes).forEach(traverse)

                if (blockTags.has(tag) && !text.endsWith('\n')) {
                    text += '\n'
                }
            }
        }
        traverse(el)
        return text.replace(/\n{2,}/g, '\n') // 去除连续空行
    }

    return Heading.configure({
        levels: [1, 2, 3, 4, 5, 6],
    }).extend({
        renderHTML({ node, HTMLAttributes }) {
            const level = node.attrs.level || 1; // 获取标题的等级
            const fold = node.attrs.fold || false; // 获取 fold 属性，默认为 false
            const getSrc = ()=>{
                if (from == 'stickynote'){
                    return fold ? getImagePathSvg("arrowRight") : getImagePathSvg("arrowBottom");
                }
                return fold ? getImagePathWithThemeSvg("arrowRight") : getImagePathWithThemeSvg("arrowBottom");
            }
            // 返回HTML结构：span + h标题
            return [
                'div', // 包裹标题和span的容器
                {
                    class: 'heading-container',
                    id: generateUUID2()
                },
                [
                    'img', // 使用 img 元素
                    {
                        class: from == 'EditorCardItem'?'heading-image-none':'heading-image', // 添加自定义样式类
                        src: getSrc(), // 设置 src 属性
                        alt: 'heading image', // 可设置 alt 文本描述
                        style:`margin-top:${level <= 2?'1px':'4px'}`
                    },
                ],
                [
                    `h${level}`,
                    mergeAttributes(HTMLAttributes),
                    0,
                ],
            ];
        },
        parseHTML() {
            return [
                {
                    tag: 'div.heading-container, h1, h2, h3,h4,h5,h6', // 选择符合条件的 HTML 标签
                    getAttrs(dom) {
                        // 直接匹配 h1, h2, h3 自身的情况 老版本笔记内容适配
                        if (dom.tagName === 'H1') return { level: 1 };
                        if (dom.tagName === 'H2') return { level: 2 };
                        if (dom.tagName === 'H3') return { level: 3 };
                        if (dom.tagName === 'H4') return { level: 4 };
                        if (dom.tagName === 'H5') return { level: 5 };
                        if (dom.tagName === 'H6') return { level: 6 };
                        const level = dom.querySelector('h1') ? 1 :
                            dom.querySelector('h2') ? 2 :
                                dom.querySelector('h3') ? 3 :
                                    dom.querySelector('h4') ? 4 :
                                        dom.querySelector('h5') ? 5 :
                                            dom.querySelector('h6') ? 6 : 6;
                        return { level, fold: dom.querySelector('[data-fold]')?.getAttribute('data-fold') === 'true', ifhidden: dom.querySelector('[ifhidden]')?.getAttribute('ifhidden') === 'true' };
                    },
                },
            ];
        },
        addProseMirrorPlugins() {
            return [
                // 确保每个新 heading 生成唯一 ID
                new Plugin({
                    key: new PluginKey("headingIdPlugin"),
                    appendTransaction: (transactions, oldState, newState) => {
                        let tr = newState.tr;
                        let modified = false;
                        newState.doc.descendants((node, pos) => {
                            if (node.type.name === "heading" && !node.attrs.id) {
                                const newId = crypto.randomUUID();
                                tr = tr.setNodeMarkup(pos, undefined, {
                                    ...node.attrs,
                                    id: newId,
                                });
                                modified = true;
                            }
                        });

                        return modified ? tr : null;
                    },
                }),
                // 新增插件：动态更新 hideImage 属性
                new Plugin({
                    key: new PluginKey("updateHideImagePlugin"),
                    view(editorView) {
                        const update = () => {
                            const titleInput =
                                document.querySelector("#NOTE_TITLE_INPUT");
                            const isTitleHidden =
                                titleInput &&
                                window.getComputedStyle(titleInput).display === "none";

                            const { state, dispatch } = editorView;
                            let tr = state.tr;
                            let modified = false;

                            state.doc.descendants((node, pos) => {
                                if (
                                    node.type.name === "heading" &&
                                    node.attrs.level === 1 &&
                                    (pos === 1 || pos === 0)
                                ) {
                                    const shouldHide = isTitleHidden;
                                    if (node.attrs.hideImage !== shouldHide) {
                                        tr = tr.setNodeMarkup(pos, undefined, {
                                            ...node.attrs,
                                            hideImage: shouldHide,
                                        });
                                        modified = true;
                                    }
                                }
                            });

                            if (modified) {
                                dispatch(tr);
                            }
                        };

                        // 初始更新
                        update();

                        // 监听可能导致标题显示/隐藏的变化
                        const observer = new MutationObserver(update);
                        const titleInput =
                            document.querySelector("#NOTE_TITLE_INPUT");
                        if (titleInput) {
                            observer.observe(titleInput, {
                                attributes: true,
                                attributeFilter: ["style"],
                            });
                        }

                        return {
                            destroy() {
                                observer.disconnect();
                            },
                        };
                    },
                }),
                new Plugin({
                    props: {
                        handleDOMEvents: {
                            click: (view, event) => {
                                const target = event.target;
                                const targetElementArr = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6']
                                if (targetElementArr.includes(target.tagName)) {
                                    document.dispatchEvent(new CustomEvent('headingClick', {
                                        detail: {
                                            id: target.getAttribute('data-id')
                                        },
                                    }))
                                }
                                if (target.tagName === 'STRONG' && targetElementArr.includes(target.parentNode.tagName)) {
                                    document.dispatchEvent(new CustomEvent('headingClick', {
                                        detail: {
                                            id: target.parentNode.getAttribute('data-id')
                                        },
                                    }))
                                }
                                if (target.tagName === 'IMG' && target.classList.contains('heading-image')) {
                                    const targetHeadingId = target.parentNode.querySelector('[data-fold]').getAttribute('id')
                                    const selectNodeNP = this.editor.$doc.querySelectorAll('heading', { id: targetHeadingId })[0]
                                    const { state, dispatch } = view;
                                    const { tr } = state;
                                    const { schema } = state;
                                    setTimeout(() => {
                                        // 改变图标的状态
                                        let tr = state.tr.setNodeMarkup(selectNodeNP.pos - 1, schema.nodes.heading, {
                                            ...selectNodeNP.attributes,
                                            fold: !selectNodeNP.attributes.fold
                                        });
                                        // 改变所属节点的隐藏显示状态
                                        // 1,确定节点所属部分
                                        let foldContentArr = []
                                        let startCompute = false
                                        state.doc.descendants((node, pos) => {
                                            if (node.type.name === 'heading' && node.attrs.id === targetHeadingId) {
                                                startCompute = true;
                                            } else if (startCompute && node.type.name !== 'text') {
                                                if (node.type.name !== 'heading' || (node.type.name === 'heading' && node.attrs.level > selectNodeNP.attributes.level)) {
                                                    foldContentArr.push({
                                                        node: node,
                                                        pos: pos
                                                    })
                                                } else {
                                                    startCompute = false;
                                                }
                                            }
                                        });
                                        // 2,改变节点所属部分的显示状态
                                        foldContentArr.forEach((ele) => {
                                            // console.log(ele.pos, 6666);

                                            tr.setNodeMarkup(ele.pos, schema.nodes[`${ele.node.type.name}`], {
                                                ...ele.node.attrs,
                                                ifhidden: selectNodeNP.attributes.fold ? false : true
                                            });

                                            // 由于table不能更新attributes属性,需要通过其他方式来改变属性值
                                            document.dispatchEvent(
                                                new CustomEvent("table-ifHidden", {
                                                    detail: {
                                                        id: ele.node.attrs.id,
                                                        ifhidden: selectNodeNP.attributes.fold
                                                            ? false
                                                            : true,
                                                    },
                                                })
                                            );
                                        })
                                        dispatch(tr);
                                        selectNodeNP.attributes.fold = !selectNodeNP.attributes.fold

                                        // 失焦
                                        this.editor.commands.blur()
                                    }, 50);
                                }
                            },
                        },
                    },
                }),
                new Plugin({
                    props: {
                        handleDOMEvents: {
                            copy(view, event) {
                                const { state } = view
                                const { selection, schema } = state
                                const { from, to } = selection
                                const resolvedPos = state.doc.resolve(from);
                                const currentNode = resolvedPos.node();
                                const parentNode = resolvedPos.node(resolvedPos.depth - 1);
                                let copyFrom = from

                                if (event.clipboardData) {
                                    if (selection instanceof CellSelection) {
                                        return false;
                                    }

                                    // 针对list复制进行特殊处理
                                    if (currentNode.type.name === 'paragraph' && ['listItem', 'taskItem'].includes(parentNode.type.name) && resolvedPos.depth >= 3 && ['bulletList', 'orderedList', 'taskList'].includes(resolvedPos.node(resolvedPos.depth - 2).type.name)) {
                                        const grandResolvedPos = state.doc.resolve(resolvedPos.start(resolvedPos.depth - 2));
                                        const grandResolvedPosFrom = grandResolvedPos.start(grandResolvedPos.depth);
                                        if (from - 2 === grandResolvedPosFrom) {
                                            copyFrom = grandResolvedPosFrom - 1
                                        }
                                    }
                                    const slice = state.doc.slice(copyFrom, to)
                                    const serializer = DOMSerializer.fromSchema(schema)
                                    const fragment = document.createDocumentFragment()
                                    slice.content.forEach(node => {
                                        const dom = serializer.serializeNode(node)
                                        if (dom) {
                                            const cloned = dom.cloneNode(true)
                                            cloned.querySelectorAll?.('.heading-image, .list-image, .task-image').forEach(img => img.remove())
                                            // 处理 latex-inline 元素：替换为 $包裹的data-metadata属性值
                                            cloned
                                                .querySelectorAll?.("latex-inline")
                                                .forEach((latex) => {
                                                    const metadata =
                                                        latex.getAttribute("data-metadata");
                                                    // if (metadata) {
                                                    const textNode = document.createTextNode(
                                                        `$${metadata}$`
                                                    );
                                                    latex.parentNode?.replaceChild(textNode, latex);
                                                    // }
                                                });

                                            // 处理 latex-block 元素：替换为 <p>$$包裹的内容</p>
                                            // 检查 cloned 本身是否是 latex-block
                                            if (cloned.tagName?.toLowerCase() === "latex-block") {
                                                const metadata =
                                                    cloned.getAttribute("data-metadata");

                                                const pElement = document.createElement("p");
                                                // 创建新的 data-id 和 ifhidden 属性
                                                pElement.setAttribute(
                                                    "data-id",
                                                    crypto.randomUUID()
                                                );
                                                pElement.setAttribute("ifhidden", "false");

                                                const textNode = document.createTextNode(
                                                    `$$${metadata}$$`
                                                );
                                                pElement.appendChild(textNode);
                                                // 用 p 元素替换 cloned
                                                fragment.appendChild(pElement);
                                            } else {
                                                fragment.appendChild(cloned);
                                            }
                                        }
                                    })

                                    // 把 fragment 插入到临时容器中，获取 HTML & text
                                    const tempDiv = document.createElement('div')
                                    tempDiv.appendChild(fragment)
                                    const html = tempDiv.innerHTML
                                    // const text = tempDiv.textContent || ''
                                    // debugger
                                    const text = extractPlainTextWithNewlines(tempDiv)
                                    event.clipboardData.setData('text/html', html)
                                    event.clipboardData.setData('text/plain', text)

                                    // setTimeout((event) => {
                                    //   event.clipboardData.setData('text/html', html)
                                    // }, 20);
                                    // event.clipboardData.setData('text/plain', text)
                                    event.preventDefault()
                                    return true
                                }
                                return false
                            }
                        },
                    },
                })
            ];
        },
        addAttributes() {
            return {
                ...this.parent?.(),
                id: {
                    default: () => {
                        const bb = crypto.randomUUID()
                        return null
                    },  // 生成唯一的 ID，只有第一次创建时生成
                    parseHTML: element => {
                        const aa = element?.lastElementChild?.getAttribute('id') || crypto.randomUUID()
                        // return element?.lastElementChild?.getAttribute('id') || crypto.randomUUID(); // 如果没有 id，从 HTML 中获取
                        return aa; // 如果没有 id，从 HTML 中获取
                    },
                    // renderHTML: attributes => {
                    //   // 在渲染时保持原有的 id
                    //   return { id: attributes.id };
                    // }
                },
                fold: {
                    default: false,
                    renderHTML: attributes => {
                        if (attributes.fold === undefined) {
                            return {};
                        }
                        return {
                            'data-fold': attributes.fold,
                        };
                    }
                },
                ifhidden: {
                    default: false,
                    renderHTML: attributes => {
                        return {
                            'ifhidden': attributes.ifhidden,
                        };
                    },
                },
                // 新增：存储是否应该隐藏图片的状态
                hideImage: {
                    default: false,
                    renderHTML: (attributes) =>
                        attributes.hideImage ? { "data-hide-image": "true" } : {},
                },
            };
        }
    })
}